import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {PanelBaseComponent} from 'projects/shared-components/panels/panel-base.component';
import {AdjustmentPricingGridComponent, ApgGridColumn} from '../adjustment-pricing-grid.component';
import {DetectMethodChanges, isVoid} from "../../utils";
import {SettingsStorageService} from "../../settings-storage-service.service";
import {MessageBusService} from "../../message-bus.service";
import {ToastrService} from "ngx-toastr";
import {UserSettingsService} from "../../user-settings.service";
import {DxScrollViewComponent} from 'devextreme-angular/ui/scroll-view';

@Component({
    selector: 'ets-adjustment-comparison-grid-panel',
    template: `
      <div [ngClass]="{'ets-hidden': !this.isActive}" [id]="this.panelId" class="panel-container">
        <dx-scroll-view direction="horizontal" showScrollbar="always" class="panel-scroll">
          <div class="root" [style.width.vw]="this.rootWidth" [style.height.px]="this.rootHeight">
            <div [ngClass]="this.leftGridCssClass">
              <ets-adjustment-pricing-grid #leftGrid orientation="left">
              </ets-adjustment-pricing-grid>
            </div>
            <div [ngClass]="this.rightGridCssClass">
              <ets-adjustment-pricing-grid #rightGrid orientation="right">
              </ets-adjustment-pricing-grid>
            </div>
          </div>
        </dx-scroll-view>
      </div>
    `,
    styles: [`
        .panel-container {
            width: 100%;
            height: 100%;
        }

        .root {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            height: 100%;
            min-width: 100vw;
            max-width: 200vw;
            column-gap: 10px;
        }

        .root .column {
            height: 100%;
            min-width: 33.1%;
            max-width: 50%;
            flex: 1;
        }

        .root .column.full {
            flex: 1;
        }

        /* Thick, always-visible bar for THE PANEL'S OWN horizontal scroll view only (2026-09-14).
           Scoped by the direct-child chain to that one widget: the first version was a bare
           ::ng-deep, which widened every DevExtreme scrollbar on the page - the Settings section's
           vertical bar included (owner, 2026-09-15: "I want it to be default"). The inner grids'
           scroll views sit deeper than this chain and keep the DevExtreme default. */
        .panel-scroll ::ng-deep > .dx-scrollable-wrapper > .dx-scrollable-container > .dx-scrollbar-horizontal.dx-scrollbar-hoverable {
            height: 15px;
        }

        .panel-scroll ::ng-deep > .dx-scrollable-wrapper > .dx-scrollable-container > .dx-scrollbar-horizontal .dx-scrollable-scroll {
            height: 15px;
        }
    `]
})
export class AdjustmentComparisonGridPanelComponent extends PanelBaseComponent {

    constructor(changeDetector: ChangeDetectorRef, userSettingsService: UserSettingsService, messageBus: MessageBusService,
                private readonly _toastr: ToastrService) {
        super(changeDetector, userSettingsService, messageBus);
    }

    @ViewChild('leftGrid') leftGrid: AdjustmentPricingGridComponent;

    @ViewChild('rightGrid') rightGrid: AdjustmentPricingGridComponent;

    // The horizontal scroll view measures its content once and never watches it. When `.root`
    // grows (a section opens on either side) the bar stayed hidden until a click made DevExtreme
    // re-measure. Every width/height change below now asks it to re-measure explicitly.
    @ViewChild(DxScrollViewComponent, {static: false})
    private _scrollView: DxScrollViewComponent;

    get leftGridCssClass(): string[] {
        const css = ['left', 'column'];
        if (this.leftGrid && !this.leftGrid.fullyCollapsed) {
            css.push('full');
        }
        return css;
    }

    get rightGridCssClass(): string[] {
        const css = ['right', 'column'];
        if (this.rightGrid && !this.rightGrid.fullyCollapsed) {
            css.push('full');
        }
        return css;
    }

    rootHeight: number

    private _rootWidth = 100;

    get rootWidth() {
        return this._rootWidth;// + '%';
    }

    etsOnInit(): void {
    }

    etsOnDestroy(): void {
    }

    etsAfterViewInit(): void {
        this.leftGrid.sectionCollapsed$.subscribe(x => {
            this.onSectionCollapsed(x, 'left');
        });
        this.rightGrid.sectionCollapsed$.subscribe(x => {
            this.onSectionCollapsed(x, 'right');
        });

        const observer = new ResizeObserver(entries => {
            if (isVoid(entries)) {
                return;
            }
            const resizeObserverEntry = entries[0];

            const height = resizeObserverEntry.contentRect.height;

            this.rootHeight = height;

            this._changeDetector.detectChanges();

            this.refreshScrollView();
        });

        const elementById = document.getElementById(this.panelId);
        observer.observe(elementById);
    }

    @DetectMethodChanges()
    onSectionCollapsed(args: {section: ApgGridColumn, state: boolean}, grid: 'left' | 'right') {

        // "Open" = visible and expanded; a section hidden through the Sections menu counts as collapsed.
        let leftCount = 0;

        if (this.leftGrid.isSectionOpen('strategies')) {
            leftCount++;
        }

        if (this.leftGrid.isSectionOpen('pricebox')) {
            leftCount++;
        }

        if (this.leftGrid.isSectionOpen('zones-grid')) {
            leftCount++;
        }

        let rightCount = 0;

        if (this.rightGrid.isSectionOpen('strategies')) {
            rightCount++;
        }

        if (this.rightGrid.isSectionOpen('pricebox')) {
            rightCount++;
        }

        if (this.rightGrid.isSectionOpen('zones-grid')) {
            rightCount++;
        }

        const maxPanelsOpen = Math.max(rightCount, leftCount);

        // let adj = 0;
        // if (maxPanelsOpen === 1) {
        //     adj  = 70;
        //     if (!this.rightGrid.zonesGridColCollapsed || !this.leftGrid.zonesGridColCollapsed) {
        //         adj = 60;
        //     }
        // } else if (maxPanelsOpen === 2) {
        //     adj = 110;
        // } else if (maxPanelsOpen === 3) {
        //     adj = 150;
        // }

        if (maxPanelsOpen > 0) {
            this._rootWidth = 190;
        } else {
            this._rootWidth= 100;
        }

        // The decorator runs detectChanges() after this method returns, so the new width is
        // not in the DOM yet. Defer the re-measure past that render.
        setTimeout(() => this.refreshScrollView());
    }

    private refreshScrollView(): void {
        const instance = this._scrollView ? this._scrollView.instance : null;
        if (instance) {
            instance.update();
        }
    }

    protected getState() {
        return null;
    }

    protected setState(state: any) {
        //
    }
}
